import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpDown, Plane, Route, Clock, Loader2, Plus, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { FarePreview } from "@/components/FarePreview";

const TABS = [
  { id: "airport", label: "Airport", icon: Plane, subs: [["airport_pickup", "Pickup from airport"], ["airport_drop", "Drop to airport"]] },
  { id: "outstation", label: "Outstation", icon: Route, subs: [["outstation_round", "Round trip"], ["outstation_oneway", "One-way"]] },
  { id: "local", label: "Local Package", icon: Clock, subs: [["local_4h", "4 Hrs / 40 KM"], ["local_8h", "8 Hrs / 80 KM"]] },
];
const AIRPORT = "Kempegowda International Airport (BLR)";
const inputCls = "w-full rounded-lg border border-slate-200 bg-white px-3.5 py-3 text-sm text-slate-900 outline-none transition-colors focus:border-[#0A192F] focus:ring-2 focus:ring-[#FFB703]/40";
const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
    {children}
  </label>
);

const minLocal = () => new Date(Date.now() + 4 * 3600 * 1000).toLocaleString("sv-SE", { timeZone: "Asia/Kolkata" }).slice(0, 16).replace(" ", "T");

export const BookingWidget = () => {
  const nav = useNavigate();
  const [tab, setTab] = useState("airport");
  const [st, setSt] = useState("airport_drop");
  const [f, setF] = useState({ pickup: "", drop: "", pickup_at: minLocal(), return_at: "", category: "Sedan", passengers: 2, luggage: "medium", route_option: "", stops: [] });
  const [quote, setQuote] = useState(null);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => { setF((p) => ({ ...p, [k]: v })); setQuote(null); };
  const pickTab = (t) => { setTab(t.id); setSt(t.subs[0][0]); setQuote(null); };
  const isAirport = st.startsWith("airport");
  const isLocal = st.startsWith("local");

  const swap = () => {
    if (isAirport) return setSt(st === "airport_pickup" ? "airport_drop" : "airport_pickup");
    setF((p) => ({ ...p, pickup: p.drop, drop: p.pickup }));
  };

  const body = () => ({
    service_type: st, category: f.category, passengers: Number(f.passengers), luggage: f.luggage,
    pickup: { address: st === "airport_pickup" ? AIRPORT : f.pickup },
    drop: isLocal ? null : { address: st === "airport_drop" ? AIRPORT : f.drop },
    pickup_at: f.pickup_at, return_at: st === "outstation_round" ? f.return_at : null,
    route_option: f.route_option || null, stops: isLocal ? f.stops : [],
  });

  const getQuote = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true); setSel(null);
    try {
      const { data } = await api.post("/quotes", body());
      setQuote(data);
      const first = data.options.find((o) => o.available && o.fare_status !== "unavailable");
      setSel(first?.rate_class || null);
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };

  const proceed = () => {
    sessionStorage.setItem("nf_booking", JSON.stringify({ req: { ...body(), raw: f }, quote, rate_class: sel, key: crypto.randomUUID() }));
    nav("/book");
  };

  const tabObj = TABS.find((t) => t.id === tab);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xl shadow-slate-900/10 sm:p-7" data-testid="booking-widget">
      <div className="mb-5 grid grid-cols-3 gap-1 rounded-xl bg-slate-100 p-1" role="tablist">
        {TABS.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => pickTab(t)} data-testid={`trip-tab-${t.id}`}
            className={`flex items-center justify-center gap-1.5 rounded-lg px-2 py-2.5 text-xs font-semibold transition-colors sm:text-sm ${tab === t.id ? "bg-[#0A192F] text-white" : "text-slate-600 hover:text-[#0A192F]"}`}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>
      <div className="mb-5 flex flex-wrap gap-2">
        {tabObj.subs.map(([id, l]) => (
          <button key={id} type="button" onClick={() => { setSt(id); setQuote(null); }} data-testid={`service-${id}`}
            className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${st === id ? "border-[#FFB703] bg-[#FFB703]/15 text-[#0A192F]" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>{l}</button>
        ))}
      </div>
      <form onSubmit={getQuote} className="space-y-4">
        <div className="relative space-y-3">
          <Field label="Pickup">
            {st === "airport_pickup" ? <div className={`${inputCls} bg-slate-50`} data-testid="pickup-airport-fixed">{AIRPORT}</div>
              : <input className={inputCls} value={f.pickup} onChange={(e) => set("pickup", e.target.value)} placeholder="Area, street or landmark (e.g. Indiranagar)" required data-testid="pickup-input" />}
          </Field>
          {!isLocal && (
            <>
              <button type="button" onClick={swap} aria-label="Swap pickup and destination" data-testid="swap-btn"
                className="absolute right-3 top-[58px] z-10 rounded-full border border-slate-200 bg-white p-2 shadow-sm transition-transform hover:rotate-180">
                <ArrowUpDown className="h-4 w-4 text-[#0A192F]" />
              </button>
              <Field label="Destination">
                {st === "airport_drop" ? <div className={`${inputCls} bg-slate-50`} data-testid="drop-airport-fixed">{AIRPORT}</div>
                  : <input className={inputCls} value={f.drop} onChange={(e) => set("drop", e.target.value)} placeholder={isAirport ? "Drop area (e.g. Whitefield)" : "City or address (e.g. Mysuru)"} required data-testid="drop-input" />}
              </Field>
            </>
          )}
        </div>
        {isLocal && (
          <div data-testid="stops-section">
            {f.stops.map((s, i) => (
              <div key={i} className="mb-2 flex gap-2">
                <input className={inputCls} value={s} placeholder={`Stop ${i + 1}`} onChange={(e) => set("stops", f.stops.map((x, j) => (j === i ? e.target.value : x)))} data-testid={`stop-input-${i}`} />
                <button type="button" onClick={() => set("stops", f.stops.filter((_, j) => j !== i))} aria-label="Remove stop" data-testid={`stop-remove-${i}`}><X className="h-4 w-4" /></button>
              </div>
            ))}
            {f.stops.length < 4 && <button type="button" onClick={() => set("stops", [...f.stops, ""])} className="flex items-center gap-1 text-xs font-semibold text-[#0A192F]" data-testid="add-stop-btn"><Plus className="h-3.5 w-3.5" /> Add stop</button>}
          </div>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Pickup date & time"><input type="datetime-local" className={inputCls} value={f.pickup_at} onChange={(e) => set("pickup_at", e.target.value)} required data-testid="pickup-datetime" /></Field>
          {st === "outstation_round" ? (
            <Field label="Return date & time"><input type="datetime-local" className={inputCls} value={f.return_at} onChange={(e) => set("return_at", e.target.value)} required data-testid="return-datetime" /></Field>
          ) : (
            <Field label="Vehicle category">
              <select className={inputCls} value={f.category} onChange={(e) => set("category", e.target.value)} data-testid="category-select">
                <option>Sedan</option><option>SUV</option><option>Hatchback</option>
              </select>
            </Field>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {st === "outstation_round" && (
            <Field label="Category">
              <select className={inputCls} value={f.category} onChange={(e) => set("category", e.target.value)} data-testid="category-select">
                <option>Sedan</option><option>SUV</option><option>Hatchback</option>
              </select>
            </Field>
          )}
          <Field label="Passengers"><input type="number" min="1" max="12" className={inputCls} value={f.passengers} onChange={(e) => set("passengers", e.target.value)} data-testid="passengers-input" /></Field>
          <Field label="Luggage">
            <select className={inputCls} value={f.luggage} onChange={(e) => set("luggage", e.target.value)} data-testid="luggage-select">
              <option value="none">None</option><option value="light">Light</option><option value="medium">Medium</option><option value="heavy">Heavy</option>
            </select>
          </Field>
          {isAirport && (
            <Field label="Route">
              <select className={inputCls} value={f.route_option} onChange={(e) => set("route_option", e.target.value)} data-testid="route-select">
                <option value="">Standard</option><option value="via_nice_road">Via NICE Road</option><option value="back_road">Back road</option>
              </select>
            </Field>
          )}
        </div>
        {err && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert" data-testid="quote-error">{err}</p>}
        <button type="submit" disabled={busy} data-testid="get-fare-btn"
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#FFB703] py-3.5 text-sm font-bold text-[#0A192F] transition-transform hover:-translate-y-0.5 disabled:opacity-60">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Check Fare
        </button>
      </form>
      {quote && (
        <div className="mt-6 border-t pt-6">
          <FarePreview quote={quote} selected={sel} onSelect={setSel} />
          <button onClick={proceed} disabled={!sel} data-testid="continue-booking-btn"
            className="mt-4 w-full rounded-xl bg-[#0A192F] py-3.5 text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-40">
            Continue to Booking
          </button>
        </div>
      )}
    </div>
  );
};
